/** @jsxImportSource @emotion/react */
import { css, Theme } from '@emotion/react';
import React, { useCallback, useEffect, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import { getIn } from 'formik';
import { useTranslation } from 'react-i18next';

import { Box, Flex } from 'components/styled';
import Button from 'components/styled/Button';

import useFieldContext from 'hooks/useFieldContext';

import { useErrorTranslation } from 'utils/localization';
import { EASMultilanguage } from 'utils/EASTypes';
import { LocalizationToLanguage } from 'utils/enumMaps';

import ErrorFeedback from '../error/ErrorFeedback';

import RTButton from './RTButtons';
import { QuillContentStyles, QuillEditorStyles } from './_styles';

type Props = {
	name: string;
	placeholder?: string;
	disabled?: boolean;
};

export const sanitizeRichText = (val: string) =>
	DOMPurify.sanitize(val, {
		// Allow YT embed
		ADD_TAGS: ['iframe'],
		ADD_ATTR: [
			'allow',
			'allowfullscreen',
			'frameborder',
			'scrolling',
			'style',
			'width',
			'height',
			'class',
			'target',
		],
	});

export const EMPTY_RICH_TEXT = '<p><br></p>';

export const isRichTextEmpty = (val?: string) =>
	(val?.replace(/<(?!iframe)[^>]*>/g, '').trim().length ?? 0) <= 0;

const toggleLanguage = (lang: 'cz' | 'en') => (lang === 'cz' ? 'en' : 'cz');

const RichTextInput: React.FC<Props> = ({ name, placeholder, disabled }) => {
	const {
		value,
		error,
		touched,
		setFieldValue,
		setFieldTouched,
		isDisabled,
	} = useFieldContext<EASMultilanguage>(name, disabled);

	// Language switch
	const { i18n } = useTranslation();
	const tError = useErrorTranslation();
	const [lang, setLang] = useState<'cz' | 'en'>(i18n.language as never);
	const [preventChange, setPreventChange] = useState(false);
	const multilang = LocalizationToLanguage[lang];

	const handeChange = useCallback(
		(val: string) => {
			if (preventChange) {
				return;
			}
			/* alternative to preventChange */
			/* if (lang === 'cz' && val === value.ENGLISH) {
				return setFieldValue({ ...value, ENGLISH: val });
			}

			if (lang === 'en' && val === value.CZECH) {
				return setFieldValue({ ...value, CZECH: val });
			} */
			return setFieldValue(
				lang === 'cz' ? { ...value, CZECH: val } : { ...value, ENGLISH: val },
			);
		},
		[lang, setFieldValue, value, preventChange],
	);

	useEffect(() => setPreventChange(false), [lang]);
	return (
		<Flex
			flexDirection="column"
			flexGrow={1}
			css={(theme: Theme) => css`
				${QuillContentStyles(theme)}
				${QuillEditorStyles(theme, !!error, touched, isDisabled)}
			`}
		>
			<Flex
				data-toolbar-for={name}
				alignItems="center"
				color="primary"
				bg="lightGrey"
				pr={2}
				pl={3}
				flexWrap="wrap"
				css={(theme: Theme) => css`
					white-space: nowrap;
					border: 0 !important;
					border-bottom: 2px solid ${theme.colors.darkerGrey} !important;
				`}
			>
				<div className="ql-formats">
					<select
						className="ql-header"
						defaultValue=""
						onChange={e => e.persist()}
					>
						<option value="1" />
						<option value="2" />
						<option />
					</select>
				</div>
				<div className="ql-formats">
					<RTButton className="ql-bold" />
					<RTButton className="ql-italic" />
					<RTButton className="ql-underline" />
					<RTButton className="ql-link" />
					<RTButton className="ql-video" />
				</div>
				<div className="ql-formats">
					<select
						className="ql-align"
						defaultValue=""
						onChange={e => e.persist()}
					>
						<option />
						<option value="center" />
						<option value="right" />
						<option value="justify" />
					</select>
				</div>
				<div className="ql-formats">
					<RTButton className="ql-list" value="ordered" />
					<RTButton className="ql-list" value="bullet" />
				</div>
				<div className="ql-formats">
					<RTButton className="ql-clean" />
				</div>
				<Box flexGrow={1} />
				{/* Toggle language button */}
				<Button
					variant="text"
					width="auto !important"
					height="auto !important"
					color={
						!!getIn(error, lang === 'cz' ? 'ENGLISH' : 'CZECH') &&
						getIn(touched, lang === 'cz' ? 'ENGLISH' : 'CZECH')
							? 'error'
							: 'primary'
					}
					onClick={() => {
						setPreventChange(true);
						return setTimeout(() => setLang(toggleLanguage), 1);
					}}
					css={css`
						text-transform: uppercase;
					`}
				>
					{toggleLanguage(lang)}
				</Button>
			</Flex>
			<ReactQuill
				id={name}
				theme="snow"
				value={value[multilang]}
				placeholder={placeholder}
				readOnly={isDisabled}
				onChange={handeChange}
				onFocus={setFieldTouched}
				modules={{
					toolbar: { container: `[data-toolbar-for="${name}"]` },
					clipboard: {
						matchVisual: false,
					},
					blotFormatter: {},
				}}
				formats={[
					'header',
					'bold',
					'italic',
					'underline',
					'align',
					'list',
					'bullet',
					'indent',
					'link',
					'video',
				]}
				css={css`
					display: flex;
					flex-grow: 1;
				`}
			/>

			{error?.[LocalizationToLanguage[lang]] && touched && (
				<ErrorFeedback
					css={(theme: Theme) => css`
						margin: ${theme.space[3]}px;
						margin-top: -${theme.space[3]}px;
					`}
				>
					{tError(error[LocalizationToLanguage[lang]])}
				</ErrorFeedback>
			)}
		</Flex>
	);
};

export default RichTextInput;
